import { useApi } from '../api/api.context';
import { useQuery } from 'react-query';
import { QueryKeys } from './query-keys.constants';
import { RestError } from '@azure/core-http';
import {
  KnownGet5ItemsItem,
  ProjectDto,
  ProjectsControllerGetSummaryResponse
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { fetchAllPages } from './query-helpers';

export const useQueryListProjects = (filters?: { permission?: string }, options?: { onSuccess?(): void }) => {
  const api = useApi();

  return useQuery(
    QueryKeys.PROJECTS,
    async () => {
      return await fetchAllPages(
        async (pageNo) =>
          await api.projectsController.list({
            pageNo,
            perPageRecords: 200,
            ...filters
          })
      );
    },
    options
  );
};

/**
 * Every network project the caller can see. Not using the API's `type` filter: it matches an
 * ancestor and returns the whole subtree, and it rejects a single value outright, so this lists
 * projects and narrows here. Keyed apart from `useQueryListProjects`, which keys on
 * `QueryKeys.PROJECTS` whatever filters it was given — sharing that entry would let a
 * permission-filtered call feed this list.
 */
export const useQueryListNetworkProjects = (options?: { enabled?: boolean }) => {
  const api = useApi();

  return useQuery<ProjectDto[], RestError>(
    [QueryKeys.PROJECTS, KnownGet5ItemsItem.Network],
    async () => {
      const projects = await fetchAllPages<ProjectDto>(
        async (pageNo) => await api.projectsController.list({ pageNo, perPageRecords: 200 })
      );
      return projects
        .filter((project) => project.projectType === KnownGet5ItemsItem.Network)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    options
  );
};

export const useQueryGetProject = (id?: string) => {
  const api = useApi();

  return useQuery<ProjectDto | undefined, RestError>([QueryKeys.PROJECTS, id], async () => {
    if (id === undefined) {
      return undefined;
    }
    return await api.projectsController.get(id);
  });
};

export const useQueryGetProjectOptional = (id: string | undefined, options?: { enabled?: boolean }) => {
  const api = useApi();

  return useQuery<ProjectDto | undefined, RestError>(
    [QueryKeys.PROJECTS, id],
    async () => {
      if (id === undefined) {
        return undefined;
      }
      return await api.projectsController.get(id);
    },
    options
  );
};

export const useQueryGetProjectSummaryOptional = (id: string | undefined, options?: { enabled?: boolean }) => {
  const api = useApi();

  return useQuery<ProjectsControllerGetSummaryResponse | undefined, RestError>(
    [QueryKeys.PROJECTS, id, QueryKeys.SUMMARY],
    async () => {
      if (id === undefined) {
        return undefined;
      }
      return await api.projectsController.getSummary(id);
    },
    options
  );
};

export const useQueryGetProjectChildProjects = (
  projectID?: string,
  options?: { onSuccess?(data: ProjectDto[]): void }
) => {
  const api = useApi();
  return useQuery<ProjectDto[], RestError>(
    [QueryKeys.PROJECTS, projectID, QueryKeys.CHILD_PROJECTS],
    async () => {
      if (!projectID) {
        return [];
      }
      return await fetchAllPages(
        async (pageNo) =>
          await api.projectsController.list({
            projectID,
            pageNo,
            perPageRecords: 200
          })
      );
    },
    {
      onSuccess: options?.onSuccess
    }
  );
};
